import { memo, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { formatDate, sentimentTone, signalRank } from './semantics';
import type { ArticleInsightLink, NewsArticle } from './types';

interface TopArticlesProps {
  articles: NewsArticle[];
  articleInsightLinks: ArticleInsightLink[];
}

const TOP_N = 4;

function SentimentPill({ sentiment }: { sentiment?: string | null }) {
  const tone = sentimentTone(sentiment);
  const n = (sentiment ?? '').toLowerCase();
  const Icon = ['positive', 'bullish'].includes(n)
    ? TrendingUp
    : ['negative', 'bearish'].includes(n)
    ? TrendingDown
    : Minus;
  const label = sentiment ? sentiment[0].toUpperCase() + sentiment.slice(1) : 'Neutral';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tone.pill}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {label}
    </span>
  );
}

function ArticleItem({
  article,
  insightCount,
}: {
  article: NewsArticle;
  insightCount: number;
}) {
  return (
    <div className="py-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-semibold leading-snug text-neutral-900">
          {article.display_title}
        </p>
        {article.url && (
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-0.5 shrink-0 rounded p-0.5 text-neutral-300 transition-colors hover:bg-neutral-50 hover:text-primary-500"
            aria-label="Open article"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
        <SentimentPill sentiment={article.sentiment} />
        {(article.domain || article.source) && (
          <span className="font-medium text-neutral-700">
            {article.domain || article.source}
          </span>
        )}
        {article.published_date && (
          <>
            <span className="text-neutral-300">·</span>
            <span className="text-neutral-500">{formatDate(article.published_date)}</span>
          </>
        )}
        {insightCount > 0 && (
          <>
            <span className="text-neutral-300">·</span>
            <span className="font-semibold text-secondary-600">
              {insightCount} business {insightCount === 1 ? 'signal' : 'signals'}
            </span>
          </>
        )}
      </div>

      {article.summary && (
        <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-neutral-500">
          {article.summary}
        </p>
      )}
    </div>
  );
}

const MemoArticleItem = memo(ArticleItem);

function TopArticlesBase({ articles, articleInsightLinks }: TopArticlesProps) {
  const [showAll, setShowAll] = useState(false);

  const insightCountMap = useMemo(() => {
    const m = new Map<string, number>();
    articleInsightLinks.forEach((l) =>
      m.set(l.article_id, (m.get(l.article_id) ?? 0) + 1)
    );
    return m;
  }, [articleInsightLinks]);

  const sorted = useMemo(
    () =>
      [...articles].sort((a, b) => {
        const sr = signalRank(b.signal_strength) - signalRank(a.signal_strength);
        if (sr !== 0) return sr;
        const da = a.published_date ? new Date(a.published_date).getTime() : 0;
        const db = b.published_date ? new Date(b.published_date).getTime() : 0;
        return db - da;
      }),
    [articles]
  );

  const visible = showAll ? sorted : sorted.slice(0, TOP_N);
  const hiddenCount = sorted.length - TOP_N;

  if (articles.length === 0) return null;

  return (
    <section>
      <div className="mb-4 flex items-baseline justify-between gap-4">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-widest text-neutral-800">
            Top Business Articles
          </h2>
          <p className="mt-1 text-xs text-neutral-500">
            Most impactful coverage, ranked by business relevance
          </p>
        </div>
        <span className="shrink-0 text-[11px] font-semibold text-neutral-500">
          {articles.length} {articles.length === 1 ? 'article' : 'articles'}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="divide-y divide-neutral-100 px-5">
          {visible.map((a) => (
            <MemoArticleItem
              key={a.article_id}
              article={a}
              insightCount={insightCountMap.get(a.article_id) ?? 0}
            />
          ))}
        </div>

        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setShowAll((o) => !o)}
            className="flex w-full items-center justify-center gap-2 border-t border-neutral-100 bg-neutral-50/60 py-3 text-xs font-semibold text-neutral-500 transition-colors hover:text-neutral-800"
          >
            {showAll ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" />
                Show fewer
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" />
                {hiddenCount} more {hiddenCount === 1 ? 'article' : 'articles'}
              </>
            )}
          </button>
        )}
      </div>
    </section>
  );
}

export const TopArticles = memo(TopArticlesBase);
